/**
 * The frame evaluator (spec 012): everything visible in a playable scene at
 * time t, as a pure function. Every renderer draws from it or is held to it
 * by the conformance fixtures (`Shared/fixtures/conformance/`).
 *
 * It declares no timing, easing or interpolation of its own: keyframes go
 * through `interpolateProperty` (spec 006), dialogs through `dialogFrameAt`
 * (spec 007), the choice reveal through `computeRevealTime` (spec 008),
 * transitions through `transitionProgress` and background motion through
 * `backgroundLayerMotion`.
 */

import type {
  FrameDialog,
  FrameLayer,
  FrameSprite,
  FrameState,
  Keyframe,
  Orientation,
  PlayableScene,
  Sprite,
} from '../@types';
import { backgroundPlaceholderName } from '../pictures/pictureMap';
import { activeKey, candidateKeyframes, interpolateProperty } from '../utils/interpolate';
import { sheetFrameAt } from '../utils/spriteSheet';
import { computeRevealTime, dialogFrameAt, lineTransitions, transitionProgress } from '../utils/sceneTiming';
import { backgroundLayerMotion } from '../utils/backgroundMotion';
import { backgroundStateAt, backgroundStatePicture, backgroundStatePlaceholder } from '../utils/backgroundStates';
import { resolveSpriteSize } from '../utils/spriteSize';
import { getResponsivePosition, getResponsiveTransform } from '../utils/responsiveLayout';
import { DEFAULT_BACKGROUND_COLOR } from '../utils/sceneContentDefaults';
import { STAGE_SIZES } from '../utils/stageSizes';

/** The stage per orientation, in stage px (the canvas buffer size). */
export { STAGE_SIZES };

export interface EvaluateFrameOptions {
  /**
   * Natural image sizes by media id: a looping background translate wraps by
   * its image's size (unwrapped when the size is unknown).
   */
  imageSizes?: Record<number, { width: number; height: number }>;
}

const finiteOr = (v: unknown, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

const picture = (id: unknown): number | null => (typeof id === 'number' && id > 0 ? id : null);

/**
 * The picture a sprite shows in `state` (spec 026 D-3): that state's image,
 * else the default state's, else null (a missing state falls back the same way).
 */
function spriteMediaId(sprite: Sprite, state: string): number | null {
  const states = sprite.states ?? [];
  return picture(states.find((s) => s.name === state)?.mediaId)
    ?? picture(states.find((s) => s.name === sprite.defaultState)?.mediaId);
}

/**
 * The state a sprite shows at `tMs` and when it began (spec 033): the active
 * `state` key's state from that key's time, ignoring keys that repeat the
 * state already showing (D-4); the default state from 0 before any key.
 */
function keyedState(sprite: Sprite, kfs: Keyframe[] | undefined, tMs: number, orientation: Orientation): { state: string; start: number } {
  const name = (v: unknown) => (typeof v === 'string' && v ? v : sprite.defaultState);
  const active = activeKey(kfs, 'state', tMs, orientation);
  if (!active) return { state: sprite.defaultState, start: 0 };
  const state = name(active.value);
  const keys = candidateKeyframes(kfs!, 'state', orientation).filter((kf) => kf.time <= tMs);
  let i = keys.indexOf(active);
  while (i > 0 && name(keys[i - 1].value) === state) i--;
  // A first key naming the default state repeats what showed from 0.
  const start = i === 0 && state === sprite.defaultState ? 0 : keys[Math.max(0, i)].time;
  return { state, start };
}

/**
 * The sheet frame of `state`, begun at `start` (spec 033). Playback never
 * switches state (D-7): a `once` that has ended holds its frame until the
 * next `state` key, and a stale `loops` or `whenDone: { goTo }` is ignored.
 * No sheet when the picture has no cut.
 */
function sheetFrame(
  scene: PlayableScene,
  sprite: Sprite,
  state: string,
  start: number,
  tMs: number,
): FrameSprite['sheet'] | undefined {
  const sheets = scene.sheets;
  if (!sheets) return undefined;
  const mediaId = spriteMediaId(sprite, state);
  const cut = mediaId === null ? undefined : sheets[mediaId];
  if (!cut) return undefined;
  const playback = (sprite.states ?? []).find((s) => s.name === state)?.playback;
  const at = sheetFrameAt(cut, playback, tMs - start, sprite.id);
  return { index: at.index, pixelArt: cut.pixelArt === true };
}

function sprites(scene: PlayableScene, tMs: number, orientation: Orientation): FrameSprite[] {
  const tracks = new Map<string, Keyframe[]>();
  for (const track of scene.keyframes) tracks.set(track.trackId, track.keyframes);

  const out = scene.sprites.map((sprite, index) => {
    const kfs = tracks.get(sprite.id);
    const num = (property: string, fallback: number) =>
      finiteOr(interpolateProperty(kfs, property, tMs, orientation), fallback);
    const position = getResponsivePosition(sprite.layout, orientation);
    const transform = getResponsiveTransform(sprite.layout, orientation, sprite.transform);
    const keyed = keyedState(sprite, kfs, tMs, orientation);
    const state = keyed.state;
    const sheet = sheetFrame(scene, sprite, state, keyed.start, tMs);
    const mediaId = spriteMediaId(sprite, state);
    const size = resolveSpriteSize(sprite);
    // Hinge keyframes are stored in percent (0–100), the sprite's hinge in 0–1.
    const hingeKf = (property: string) => {
      const v = interpolateProperty(kfs, property, tMs, orientation);
      return typeof v === 'number' && Number.isFinite(v) ? v / 100 : undefined;
    };
    // Flips step (spec 038 D-3): the key in force, else the at-rest flip; any value but 0 is flipped.
    const flip = (property: string, atRest: boolean | undefined) => {
      const v = interpolateProperty(kfs, property, tMs, orientation);
      return v === undefined ? !!atRest : v !== 0 && v !== '0';
    };
    const frame: FrameSprite = {
      id: sprite.id,
      mediaId,
      state,
      ...(mediaId === null ? { placeholder: `${sprite.name ?? ''} · ${state}` } : {}),
      x: num('position.x', finiteOr(position.x, 50)),
      y: num('position.y', finiteOr(position.y, 50)),
      width: size.width,
      height: size.height,
      hingeX: hingeKf('hinge.x') ?? finiteOr(sprite.hinge?.x, 0.5),
      hingeY: hingeKf('hinge.y') ?? finiteOr(sprite.hinge?.y, 0.5),
      scale: num('transform.scale', finiteOr(transform.scale, 1)),
      rotation: num('transform.rotation', finiteOr(transform.rotation, 0)),
      flipX: flip('transform.flipX', transform.flipX),
      flipY: flip('transform.flipY', transform.flipY),
      opacity: num('opacity', 1),
      zIndex: finiteOr(sprite.zIndex, 0),
      ...(sheet ? { sheet } : {}),
    };
    return { frame, index };
  });
  out.sort((a, b) => a.frame.zIndex - b.frame.zIndex || a.index - b.index);
  return out.map((s) => s.frame);
}

function dialogs(scene: PlayableScene, tMs: number): FrameDialog[] {
  const out: FrameDialog[] = [];
  for (const dialog of scene.dialogs) {
    const f = dialogFrameAt(dialog, tMs);
    const panelOpacity = clamp01(f.panelProgress);
    if (f.panelPhase === 'hidden' || !(panelOpacity > 0)) continue;
    let text = '';
    let textOpacity = 0;
    if (f.line && f.phase !== 'hidden') {
      const full = f.line.text ?? '';
      const { inTransition, outTransition } = lineTransitions(dialog, f.line);
      text = full;
      textOpacity = panelOpacity;
      if (f.phase === 'in' && inTransition === 'typing') {
        text = full.substring(0, Math.floor(full.length * clamp01(f.progress)));
      } else if (f.phase === 'out' && outTransition === 'typing') {
        text = full.substring(0, Math.floor(full.length * (1 - clamp01(f.progress))));
      } else if (f.phase === 'in' && inTransition === 'fade') {
        textOpacity = panelOpacity * clamp01(f.progress);
      } else if (f.phase === 'out' && outTransition === 'fade') {
        textOpacity = panelOpacity * (1 - clamp01(f.progress));
      }
    }
    out.push({
      id: dialog.id,
      position: dialog.position === 'top' ? 'top' : 'bottom',
      panelOpacity,
      lineId: f.line?.id ?? null,
      text,
      textOpacity,
    });
  }
  return out;
}

/**
 * The background: colour and layers, the base image when set, the
 * placeholder label by D-11 (spec 026). A background with states (spec 039)
 * shows the playing state's picture (or its `<name> · <state>` placeholder)
 * and, while a change runs, the change from the picture showing before.
 */
function frameBackground(scene: PlayableScene, layers: FrameLayer[], tMs: number): FrameState['background'] {
  const bg: FrameState['background'] = { color: scene.background?.color || DEFAULT_BACKGROUND_COLOR, layers };
  const at = backgroundStateAt(scene.background, scene.keyframes, tMs);
  if (at.state === undefined) {
    const base = picture(scene.background?.baseMediaId);
    if (base !== null) bg.baseMediaId = base;
    const placeholder = backgroundPlaceholderName(scene.background);
    if (placeholder !== undefined) bg.placeholder = placeholder;
    return bg;
  }
  bg.state = at.state;
  const base = backgroundStatePicture(scene.background, at.state);
  if (base !== undefined) bg.baseMediaId = base;
  else {
    const placeholder = backgroundStatePlaceholder(scene.background, at.state);
    if (placeholder !== undefined) bg.placeholder = placeholder;
  }
  if (at.change) {
    const from = backgroundStatePicture(scene.background, at.change.from);
    const fromPlaceholder = from === undefined ? backgroundStatePlaceholder(scene.background, at.change.from) : undefined;
    bg.change = {
      ...(from !== undefined ? { fromMediaId: from } : {}),
      ...(fromPlaceholder !== undefined ? { fromPlaceholder } : {}),
      type: at.change.type,
      ...(at.change.direction ? { direction: at.change.direction } : {}),
      progress: at.change.progress,
    };
  }
  return bg;
}

/**
 * The visible state of `scene` at `tMs` in `orientation`. Pure: the same
 * scene, t, orientation and options always give the same state, whatever
 * was evaluated before.
 */
export function evaluateFrame(
  scene: PlayableScene,
  tMs: number,
  orientation: Orientation,
  opts: EvaluateFrameOptions = {},
): FrameState {
  const stage = STAGE_SIZES[orientation] ?? STAGE_SIZES.portrait;
  const layers: FrameLayer[] = (scene.background?.layers ?? []).map((layer) => {
    const motion = backgroundLayerMotion(layer, tMs, opts.imageSizes?.[layer.mediaId]);
    return {
      id: layer.id,
      mediaId: finiteOr(layer.mediaId, 0),
      opacity: finiteOr(layer.opacity, 1),
      ...motion,
    };
  });
  const choices = scene.choices ?? [];
  return {
    t: tMs,
    orientation,
    stage: { width: stage.width, height: stage.height },
    transition: {
      enter: transitionProgress(scene.enterTransition, 'enter', tMs, scene.duration),
      exit: transitionProgress(scene.exitTransition, 'exit', tMs, scene.duration),
    },
    background: frameBackground(scene, layers, tMs),
    sprites: sprites(scene, tMs, orientation),
    dialogs: dialogs(scene, tMs),
    choices: {
      visible: choices.length > 0 && tMs >= computeRevealTime(scene.duration, scene.choiceSettings?.showBeforeEnd, scene.exitTransition),
      optionIds: choices.map((c) => c.id),
    },
  };
}
